/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Stack,
  Container,
  Typography,
  Card,
  Grid,
  Switch,
  Button,
  Chip,
  alpha,
  useTheme,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  CircularProgress,
  MenuItem,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  Tooltip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Divider
} from '@mui/material';
import {
  LocalOffer as PromoIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  ContentCopy as CopyIcon,
  Percent as PercentIcon,
  AttachMoney as MoneyIcon,
  CalendarToday as CalIcon,
  GroupWork as GroupIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { getApi, postApi, putApi, deleteApi, patchApi } from '../services/api';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const formatDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: '2-digit'
    });
  } catch {
    return '—';
  }
};

const blankForm = {
  code: '',
  description: '',
  discountType: 'percentage',
  discountValue: 10,
  maxUses: '',
  validUntil: '',
  oneUsePerHotel: true,
  applicableSubscriptions: [],
  enabled: true
};

const AdminPlatformPromoCodes = () => {
  const theme = useTheme();
  const [promoCodes, setPromoCodes] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(blankForm);
  const [submitting, setSubmitting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // -------------------------------------------------------------------------
  // Data loading
  // -------------------------------------------------------------------------
  const loadData = async () => {
    setLoading(true);
    try {
      const [promoRes, pkgRes] = await Promise.all([
        getApi('api/platform-promo-codes/'),
        getApi('api/packages/getAllPackages')
      ]);
      if (promoRes?.status === 200) {
        setPromoCodes(promoRes.data?.promoCodes || []);
      }
      if (pkgRes?.status === 200) {
        setPackages(pkgRes.data?.packagesAllData || []);
      } else if (pkgRes?.status === 204) {
        setPackages([]);
      }
    } catch (e) {
      console.error(e);
      toast.error('Error cargando datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // -------------------------------------------------------------------------
  // Stats
  // -------------------------------------------------------------------------
  const stats = useMemo(() => {
    const total = promoCodes.length;
    const enabled = promoCodes.filter((p) => p.enabled).length;
    const totalRedemptions = promoCodes.reduce((acc, p) => acc + (p.usedCount || 0), 0);
    return { total, enabled, totalRedemptions };
  }, [promoCodes]);

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------
  const openCreate = () => {
    setEditingId(null);
    setForm(blankForm);
    setOpenDialog(true);
  };

  const openEdit = (promo) => {
    setEditingId(promo._id);
    setForm({
      code: promo.code || '',
      description: promo.description || '',
      discountType: promo.discountType || 'percentage',
      discountValue: promo.discountValue || 0,
      maxUses: promo.maxUses == null ? '' : String(promo.maxUses),
      validUntil: promo.validUntil ? new Date(promo.validUntil).toISOString().slice(0, 10) : '',
      oneUsePerHotel: !!promo.oneUsePerHotel,
      applicableSubscriptions: (promo.applicableSubscriptions || []).map((s) =>
        typeof s === 'object' ? s._id : s
      ),
      enabled: !!promo.enabled
    });
    setOpenDialog(true);
  };

  const handleClose = () => {
    if (submitting) return;
    setOpenDialog(false);
  };

  const handleChange = (key) => (e) => {
    const value = e?.target?.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const handleToggleSubscription = (id) => {
    setForm((f) => {
      const set = new Set(f.applicableSubscriptions);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return { ...f, applicableSubscriptions: Array.from(set) };
    });
  };

  const handleSubmit = async () => {
    if (!form.code.trim()) {
      toast.error('El código es requerido');
      return;
    }
    if (form.discountType === 'percentage') {
      if (Number(form.discountValue) <= 0 || Number(form.discountValue) > 100) {
        toast.error('El porcentaje debe ser entre 1 y 100');
        return;
      }
    } else if (Number(form.discountValue) <= 0) {
      toast.error('El monto fijo debe ser mayor a 0');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...form,
        discountValue: Number(form.discountValue),
        maxUses: form.maxUses === '' ? null : Number(form.maxUses),
        validUntil: form.validUntil || null
      };
      const res = editingId
        ? await putApi(`api/platform-promo-codes/${editingId}`, payload)
        : await postApi('api/platform-promo-codes/', payload);

      if (res?.status === 200 || res?.status === 201) {
        toast.success(editingId ? 'Código actualizado' : 'Código creado');
        setOpenDialog(false);
        loadData();
      } else {
        toast.error(res?.response?.data?.error || res?.data?.error || 'No se pudo guardar');
      }
    } catch (e) {
      console.error(e);
      toast.error('Error guardando el código');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleEnabled = async (promo) => {
    try {
      const res = await patchApi(`api/platform-promo-codes/${promo._id}/toggle`, {});
      if (res?.status === 200) {
        toast.success(`Código ${res.data?.promoCode?.enabled ? 'activado' : 'desactivado'}`);
        loadData();
      }
    } catch (e) {
      toast.error('Error actualizando estado');
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      const res = await deleteApi('api/platform-promo-codes/', confirmDelete._id);
      if (res?.status === 200) {
        toast.success('Código eliminado');
        loadData();
      }
    } catch (e) {
      toast.error('Error eliminando');
    } finally {
      setConfirmDelete(null);
    }
  };

  const handleCopy = (code) => {
    navigator.clipboard?.writeText(code);
    toast.success(`"${code}" copiado al portapapeles`);
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.warning.main, 0.12),
              color: 'warning.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <PromoIcon sx={{ fontSize: 32 }} />
          </Box>
          <Box>
            <Typography variant="h4" fontWeight={700}>
              Códigos Promocionales
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Crea ofertas para atraer hoteles a la plataforma. Los códigos se aplican al adquirir un plan.
            </Typography>
          </Box>
        </Stack>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Nuevo código
        </Button>
      </Stack>

      {/* Stats */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={4}>
          <Paper sx={{ p: 2.5, borderRadius: 2 }}>
            <Typography variant="overline" color="text.secondary">
              Total códigos
            </Typography>
            <Typography variant="h3" fontWeight={700}>
              {stats.total}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper sx={{ p: 2.5, borderRadius: 2 }}>
            <Typography variant="overline" color="text.secondary">
              Activos
            </Typography>
            <Typography variant="h3" fontWeight={700} color="success.main">
              {stats.enabled}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper sx={{ p: 2.5, borderRadius: 2 }}>
            <Typography variant="overline" color="text.secondary">
              Canjes totales
            </Typography>
            <Typography variant="h3" fontWeight={700} color="primary.main">
              {stats.totalRedemptions}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Tabla */}
      <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {loading ? (
          <Stack alignItems="center" py={6}>
            <CircularProgress />
          </Stack>
        ) : promoCodes.length === 0 ? (
          <Stack alignItems="center" py={6} spacing={1.5}>
            <PromoIcon sx={{ fontSize: 48, color: alpha(theme.palette.text.secondary, 0.4) }} />
            <Typography variant="body1" color="text.secondary">
              Aún no has creado códigos promocionales
            </Typography>
            <Button variant="outlined" onClick={openCreate} startIcon={<AddIcon />}>
              Crear el primero
            </Button>
          </Stack>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                  <TableCell>Código</TableCell>
                  <TableCell>Descuento</TableCell>
                  <TableCell>Usos</TableCell>
                  <TableCell>Validez</TableCell>
                  <TableCell>Aplica a</TableCell>
                  <TableCell align="center">Activo</TableCell>
                  <TableCell align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {promoCodes.map((p) => {
                  const isExpired = p.validUntil && new Date(p.validUntil) < new Date();
                  const isExhausted = p.maxUses != null && p.usedCount >= p.maxUses;
                  return (
                    <TableRow key={p._id} hover>
                      <TableCell>
                        <Stack spacing={0.3}>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Typography
                              variant="body2"
                              fontWeight={700}
                              sx={{ fontFamily: 'monospace', letterSpacing: 0.5 }}
                            >
                              {p.code}
                            </Typography>
                            <Tooltip title="Copiar">
                              <IconButton size="small" onClick={() => handleCopy(p.code)}>
                                <CopyIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                          {p.description && (
                            <Typography variant="caption" color="text.secondary">
                              {p.description}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          icon={
                            p.discountType === 'percentage' ? <PercentIcon /> : <MoneyIcon />
                          }
                          label={
                            p.discountType === 'percentage'
                              ? `${p.discountValue}%`
                              : `$${p.discountValue}`
                          }
                          color={p.discountType === 'percentage' ? 'warning' : 'success'}
                          sx={{ fontWeight: 700 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {p.usedCount || 0}
                          {p.maxUses != null ? ` / ${p.maxUses}` : ' / ∞'}
                        </Typography>
                        {p.oneUsePerHotel && (
                          <Typography variant="caption" color="text.secondary">
                            1 por hotel
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {p.validUntil ? (
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <CalIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography
                              variant="caption"
                              color={isExpired ? 'error.main' : 'text.primary'}
                            >
                              {formatDate(p.validUntil)}
                            </Typography>
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Sin expiración
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {p.applicableSubscriptions && p.applicableSubscriptions.length > 0 ? (
                          <Tooltip
                            title={p.applicableSubscriptions
                              .map((s) => (typeof s === 'object' ? s.title : s))
                              .join(', ')}
                          >
                            <Chip
                              size="small"
                              icon={<GroupIcon />}
                              label={`${p.applicableSubscriptions.length} plan(es)`}
                              variant="outlined"
                            />
                          </Tooltip>
                        ) : (
                          <Chip size="small" label="Todos los planes" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Switch
                          checked={!!p.enabled}
                          onChange={() => handleToggleEnabled(p)}
                          color="success"
                        />
                        {(isExpired || isExhausted) && (
                          <Typography variant="caption" color="error.main" display="block">
                            {isExpired ? 'Expirado' : 'Agotado'}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => openEdit(p)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setConfirmDelete(p)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* Dialog crear/editar */}
      <Dialog open={openDialog} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pr: 6 }}>
          {editingId ? 'Editar código promocional' : 'Nuevo código promocional'}
          <IconButton
            onClick={handleClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} pt={1}>
            <TextField
              label="Código"
              fullWidth
              size="small"
              value={form.code}
              onChange={handleChange('code')}
              required
              helperText="Se guardará en mayúsculas. Ej: LAUNCH20"
              inputProps={{ style: { textTransform: 'uppercase', letterSpacing: 1 } }}
            />
            <TextField
              label="Descripción interna"
              fullWidth
              size="small"
              value={form.description}
              onChange={handleChange('description')}
              helperText="Solo visible para administradores"
            />

            <Stack direction="row" spacing={2}>
              <TextField
                select
                label="Tipo"
                size="small"
                value={form.discountType}
                onChange={handleChange('discountType')}
                sx={{ flex: 1 }}
              >
                <MenuItem value="percentage">Porcentaje (%)</MenuItem>
                <MenuItem value="fixed">Monto fijo (USD)</MenuItem>
              </TextField>
              <TextField
                label="Valor"
                type="number"
                size="small"
                value={form.discountValue}
                onChange={handleChange('discountValue')}
                sx={{ flex: 1 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {form.discountType === 'percentage' ? '%' : 'USD'}
                    </InputAdornment>
                  )
                }}
              />
            </Stack>

            <Stack direction="row" spacing={2}>
              <TextField
                label="Máximo de usos totales"
                type="number"
                size="small"
                value={form.maxUses}
                onChange={handleChange('maxUses')}
                placeholder="Ilimitado"
                sx={{ flex: 1 }}
                helperText="Vacío = ilimitado"
              />
              <TextField
                label="Válido hasta"
                type="date"
                size="small"
                value={form.validUntil}
                onChange={handleChange('validUntil')}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
                helperText="Vacío = sin expiración"
              />
            </Stack>

            <FormControlLabel
              control={
                <Switch
                  checked={form.oneUsePerHotel}
                  onChange={handleChange('oneUsePerHotel')}
                />
              }
              label="Un solo uso por hotel"
            />

            <Box>
              <Typography variant="subtitle2" mb={1}>
                Planes a los que aplica
              </Typography>
              {packages.length === 0 && (
                <Typography variant="caption" color="text.secondary">
                  No hay planes configurados. Crea suscripciones primero.
                </Typography>
              )}
              {packages.length > 0 && (
                <>
                  <Typography variant="caption" color="text.secondary" mb={1} display="block">
                    Si no seleccionas ninguno, el código aplicará a TODOS los planes.
                  </Typography>
                  <Stack
                    spacing={0.5}
                    sx={{ maxHeight: 180, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}
                  >
                    {packages.map((pkg) => (
                      <FormControlLabel
                        key={pkg._id}
                        control={
                          <Checkbox
                            size="small"
                            checked={form.applicableSubscriptions.includes(pkg._id)}
                            onChange={() => handleToggleSubscription(pkg._id)}
                          />
                        }
                        label={
                          <Typography variant="body2">
                            {pkg.title}{' '}
                            <Typography component="span" variant="caption" color="text.secondary">
                              ({pkg.days} días — ${pkg.amount})
                            </Typography>
                          </Typography>
                        }
                      />
                    ))}
                  </Stack>
                </>
              )}
            </Box>

            <Divider />

            <FormControlLabel
              control={
                <Switch checked={form.enabled} onChange={handleChange('enabled')} color="success" />
              }
              label="Activo (los hoteles pueden usarlo)"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} disabled={submitting} color="inherit">
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={handleSubmit}
            disabled={submitting}
          >
            {editingId ? 'Guardar' : 'Crear código'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmación borrar */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>¿Eliminar código?</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Seguro que quieres eliminar el código <strong>{confirmDelete?.code}</strong>? Los pagos
            ya realizados con este código se conservan, pero ningún hotel podrá usarlo de nuevo.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminPlatformPromoCodes;
